import React, {useState} from 'react'
import './ImportLabelPopup.scss'
import {LabelType} from "../../../data/enums/LabelType";
import {PopupActions} from "../../../logic/actions/PopupActions";
import GenericLabelTypePopup from "../GenericLabelTypePopup/GenericLabelTypePopup";
import {ImportFormatData} from "../../../data/ImportFormatData";
import {FeatureInProgress} from "../../EditorView/FeatureInProgress/FeatureInProgress";
import {AppState} from "../../../store";
import {connect} from "react-redux";
import {useDropzone} from "react-dropzone";
import {AcceptedFileType} from "../../../data/enums/AcceptedFileType";
import {ImageData, LabelName} from "../../../store/labels/types";
import {updateActiveLabelType, updateImageData, updateLabelNames} from "../../../store/labels/actionCreators";
import {ImporterSpecData} from "../../../data/ImporterSpecData";
import {AnnotationFormatType} from "../../../data/enums/AnnotationFormatType";
import {ILabelFormatData} from "../../../interfaces/ILabelFormatData";

interface IProps {
    activeLabelType: LabelType,
    updateImageData: (imageData: ImageData[]) => any,
    updateLabelNames: (labels: LabelName[]) => any,
    updateActiveLabelType: (activeLabelType: LabelType) => any
}

const ImportLabelPopup: React.FC<IProps> = (
    {
        activeLabelType,
        updateImageData,
        updateLabelNames,
        updateActiveLabelType
    }) => {
    const resolveFormatType = (labelType: LabelType): AnnotationFormatType => {
        const possibleImportFormats = ImportFormatData[labelType]
        return possibleImportFormats.length === 1 ? possibleImportFormats[0].type : null
    }

    const [labelType, setLabelType] = useState(activeLabelType);
    const [formatType, setFormatType] = useState(resolveFormatType(activeLabelType));
    const [loadedLabelNames, setLoadedLabelNames] = useState([]);
    const [loadedImageData, setLoadedImageData] = useState([]);
    const [annotationsLoadedError, setAnnotationsLoadedError] = useState(null);

    var locale = localStorage.getItem("locale");

    const {getRootProps, getInputProps} = useDropzone({
        accept: [AcceptedFileType.JSON, AcceptedFileType.TEXT],
        multiple: true,
        onDrop: (acceptedFiles) => {
            const importer = new (ImporterSpecData[formatType])([labelType])
            importer.import(acceptedFiles, onAnnotationLoadSuccess, onAnnotationsLoadFailure);
        }
    });

    const onLabelTypeChange = (labelType: LabelType) => {
        setLabelType(labelType);
        setFormatType(resolveFormatType(labelType))
        setLoadedLabelNames([]);
        setLoadedImageData([]);
        setAnnotationsLoadedError(null);
    }

    const onAnnotationLoadSuccess = (imagesData: ImageData[], labelNames: LabelName[]) => {
        setLoadedLabelNames(labelNames);
        setLoadedImageData(imagesData);
        setAnnotationsLoadedError(null);
    }

    const onAnnotationsLoadFailure = (error?:Error) => {
        setLoadedLabelNames([]);
        setLoadedImageData([]);
        setAnnotationsLoadedError(error);
    };

    const onAccept = (labelType: LabelType) => {
        if (loadedLabelNames.length !== 0 && loadedImageData.length !== 0) {
            updateImageData(loadedImageData);
            updateLabelNames(loadedLabelNames);
            updateActiveLabelType(labelType);
            let list = []
            for (var i = 0; i < loadedImageData.length; i++) {
                list.push("1")
            }
            localStorage.setItem("isSelectLabel", JSON.stringify(list))
            PopupActions.close();
        }
    };

    const onReject = (labelType: LabelType) => {
        PopupActions.close();
    };

    const onAnnotationFormatChange = (format: AnnotationFormatType) => {
        setFormatType(format);
    }

    const getDropZoneContent = () => {
        if (!!annotationsLoadedError) {
            return <>
                <input {...getInputProps()} />
                <img
                    draggable={false}
                    alt={"upload"}
                    src={"ico/box-opened.png"}
                />
                <p className="extraBold">{locale == "en" ? "Annotation import was unsuccessful" : (locale == "zh-Hans" ? "标记文件导入失败" : "標記文件導入失敗")}</p>
                {annotationsLoadedError.message}
                <p className="extraBold">{locale == "en" ? "Try again" : (locale == "zh-Hans" ? "再试一次" : "再試一次")}</p>
            </>;
        } else if (loadedImageData.length !== 0 && loadedLabelNames.length !== 0) {
            return <>
                <img
                    draggable={false}
                    alt={"uploaded"}
                    src={"ico/box-closed.png"}
                />
                <p className="extraBold">{locale == "en" ? "Annotation ready for import" : (locale == "zh-Hans" ? "准备导入标记文件" : "準備導入標記文件")}</p>
                
                {locale == "en" ? "After import you will lose" +
                "all your current annotations" : (locale == "zh-Hans" ? "导入后，您将丢失所有当前标记项" : "導入後，您將丟失所有當前標記項")}
            </>;
        } else {
            return <>
                <input {...getInputProps()} />
                <img
                    draggable={false}
                    alt={"upload"}
                    src={"ico/box-opened.png"}
                />
                <p className="extraBold">{locale == "en" ? `Drop ${formatType} annotations`:(locale == "zh-Hans" ? `拖拽${formatType}标记文件` : `拖拽${formatType}標記文件`)}</p>
                <p>or</p>
                <p className="extraBold">{locale == "en"?"Click here to select them":(locale == "zh-Hans"?"单击此处选择标记文件":"單擊此處選擇標記文件")}</p>
            </>;
        }
    };

    const getOptions = (exportFormatData: ILabelFormatData[]) => {
        return exportFormatData.map((entry: ILabelFormatData) => {
            return <div
                className="OptionsItem"
                onClick={() => onAnnotationFormatChange(entry.type)}
                key={entry.type}
            >
                {entry.type === formatType ?
                    <img
                        draggable={false}
                        src={"ico/checkbox-checked.png"}
                        alt={"checked"}
                    /> :
                    <img
                        draggable={false}
                        src={"ico/checkbox-unchecked.png"}
                        alt={"unchecked"}
                    />}
                {entry.label}
            </div>
        })
    };

    const renderInternalContent = (labelType: LabelType) => {
        if (!formatType && ImportFormatData[labelType].length !== 0) {
            return [
                <div className="Message">
                    Select file format you would like to use to import labels.
                </div>,
                <div className="Options">
                    {getOptions(ImportFormatData[labelType])}
                </div>
            ]
        }
        const importFormatData = ImportFormatData[labelType];
        return importFormatData.length === 0 ?
            <FeatureInProgress/> :
            <div {...getRootProps({className: 'DropZone'})}>
                {getDropZoneContent()}
            </div>
    }

    return(
        <GenericLabelTypePopup
            activeLabelType={labelType}
            title={locale == "en" ? `Import ${formatType} annotations`:(locale == "zh-Hans" ? `导入${formatType}标记文件` : `導入${formatType}標記文件`)}
            onLabelTypeChange={onLabelTypeChange}
            acceptLabel={locale == "en" ? "Import":(locale == "zh-Hans" ? "导入" : "導入")}
            onAccept={onAccept}
            skipAcceptButton={ImportFormatData[labelType].length === 0}
            disableAcceptButton={loadedImageData.length === 0 || loadedLabelNames.length === 0 || !!annotationsLoadedError}
            rejectLabel={locale == "en" ? "Cancel":(locale == "zh-Hans" ? "取消" : "取消")}
            onReject={onReject}
            renderInternalContent={renderInternalContent}
        />
    )
};

const mapDispatchToProps = {
    updateImageData,
    updateLabelNames,
    updateActiveLabelType
};

const mapStateToProps = (state: AppState) => ({
    activeLabelType: state.labels.activeLabelType,
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ImportLabelPopup);